import React, { useState } from 'react';
// v1.6 - Fixed PDF download with setup instructions

function PDFLeadMagnet({ isOpen, onClose }) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      setIsSubmitting(false);
      return;
    }

    if (!firstName.trim()) {
      setError('Please enter your first name');
      setIsSubmitting(false);
      return;
    }

    try {
      // ⚠️ MAILCHIMP SETUP - Must match the setup in LeadMagnetPage.js
      // This should be the same Mailchimp URL you configured there
      const MAILCHIMP_URL = 'YOUR_MAILCHIMP_URL_HERE';
      
      if (MAILCHIMP_URL !== 'YOUR_MAILCHIMP_URL_HERE') {
        // Convert to JSONP endpoint to avoid CORS issues
        const jsonpUrl = MAILCHIMP_URL.replace('/post?', '/post-json?') + 
          `&EMAIL=${encodeURIComponent(email)}&FNAME=${encodeURIComponent(firstName)}`;
        
        // Submit to Mailchimp
        await fetch(jsonpUrl, {
          method: 'GET',
          mode: 'no-cors',
        });
      } else {
        // Mailchimp not set up yet - log to console for testing
        console.log('PDF Lead Magnet submitted:', { email, firstName });
      }
      
      setIsSuccess(true);

      // Track the conversion
      if (window.gtag) {
        window.gtag('event', 'generate_lead', {
          event_category: 'Lead Magnet',
          event_label: 'People-Pleasing Therapy Room Guide',
        });
      }

      // Redirect to thank you page
      setTimeout(() => {
        window.location.href = '/thank-you';
      }, 1500);

    } catch (err) {
      console.error('Form submission error:', err);
      setError('Something went wrong. Please try again.');
    }

    setIsSubmitting(false);
  };

  const handleDownload = () => {
    // ⚠️ PDF FILE SETUP REQUIRED:
    // Replace with your actual PDF URL
    const pdfUrl = 'YOUR_PDF_URL_HERE';
    
    if (pdfUrl === 'YOUR_PDF_URL_HERE') {
      alert('⚠️ PDF Setup Required\n\nPlease upload your PDF and update the download link in:\nsrc/components/PDFLeadMagnet.jsx (line 77)');
      return;
    }
    
    // Trigger PDF download
    window.open(pdfUrl, '_blank');
    
    // Close modal after a short delay
    setTimeout(() => {
      onClose();
      // Reset form for next time
      setEmail('');
      setFirstName('');
      setIsSuccess(false);
    }, 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
          aria-label="Close"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header with gradient */}
        <div className="bg-gradient-to-br from-warm-500 to-sage-500 p-8 text-white">
          <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center mb-4">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-2xl font-serif font-light mb-2">
            Download Your Free Guide
          </h3>
          <p className="text-white text-opacity-90 text-sm">
            Get instant access to "People-Pleasing in the Therapy Room"
          </p>
        </div>

        {/* Form or Success State */}
        <div className="p-8">
          {!isSuccess ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-warm-500 focus:border-transparent transition-all"
                  placeholder="Enter your first name"
                  required
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-warm-500 focus:border-transparent transition-all"
                  placeholder="you@example.com"
                  required
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-warm-500 to-sage-500 text-white py-4 rounded-lg font-medium hover:from-warm-600 hover:to-sage-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                {isSubmitting ? 'Sending...' : 'Get the Free PDF'}
              </button>

              <p className="text-xs text-gray-500 text-center mt-4">
                I respect your privacy. Unsubscribe anytime.
              </p>
            </form>
          ) : (
            <div className="text-center py-4">
              {/* Success checkmark animation */}
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              <h4 className="text-xl font-semibold text-gray-800 mb-2">
                Thank you, {firstName}!
              </h4>
              <p className="text-gray-600 mb-6">
                Your guide is ready to download.
              </p>

              <button
                onClick={handleDownload}
                className="w-full bg-gradient-to-r from-warm-500 to-sage-500 text-white py-4 rounded-lg font-medium hover:from-warm-600 hover:to-sage-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Download PDF Now
              </button>

              <p className="text-sm text-gray-500 mt-4">
                Check your email for a copy of the download link too!
              </p>
            </div>
          )}
        </div>

        {/* What's Inside Preview */}
        {!isSuccess && (
          <div className="bg-gray-50 px-8 py-6 border-t border-gray-200">
            <p className="text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wide">
              What's Inside:
            </p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start">
                <svg className="w-5 h-5 text-warm-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                How power dynamics shape therapy
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-warm-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Intersectional identities in the therapy room
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-warm-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                What culturally humble therapists do differently
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-warm-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Questions to ask potential therapists
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default PDFLeadMagnet;
